import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Search, Loader2, Info, SlidersHorizontal, Crown, Camera } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { rupiah } from "../lib/format";
import { useAuth } from "../context/AuthContext";

const SORTS = [
  { key: "relevan", label: "Paling Sesuai" },
  { key: "termurah", label: "Termurah" },
  { key: "termahal", label: "Termahal" },
];

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [results, setResults] = useState([]);
  const [state, setState] = useState({ loading: false, done: false, msg: "" });
  const [sort, setSort] = useState("relevan");
  const [minP, setMinP] = useState("");
  const [maxP, setMaxP] = useState("");
  const [member, setMember] = useState(true);
  const nav = useNavigate();
  const { user } = useAuth();

  const doSearch = async (term) => {
    if (!term.trim()) return;
    setState({ loading: true, done: false, msg: "" });
    try {
      const { data } = await api.get("/alibaba/search", { params: { q: term } });
      setMember(!!data.member);
      if (!data.enabled) setState({ loading: false, done: true, msg: data.message || "Katalog belum aktif." });
      else { setResults(data.results); setState({ loading: false, done: true, msg: data.results.length ? "" : "Tidak ada hasil, coba kata kunci lain." }); }
    } catch (e2) { setState({ loading: false, done: true, msg: apiError(e2) }); }
  };

  const onImageSearch = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!user) { toast.error("Masuk dulu untuk cari pakai gambar"); nav("/login"); return; }
    setState({ loading: true, done: false, msg: "" });
    try {
      const fd = new FormData();
      fd.append("file", f);
      const up = await api.post("/uploads", fd);
      const { data } = await api.get("/alibaba/search_image", { params: { path: up.data.path } });
      setMember(!!data.member);
      setResults(data.results || []);
      setState({ loading: false, done: true, msg: (data.results && data.results.length) ? "" : "Tidak ada barang mirip. Coba foto yang lebih jelas / fokus ke satu barang." });
    } catch (err) { setState({ loading: false, done: true, msg: apiError(err) }); }
    finally { e.target.value = ""; }
  };

  useEffect(() => {
    const term = params.get("q") || "";
    setQ(term);
    if (term) doSearch(term);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const submit = (e) => { e.preventDefault(); if (q.trim()) setParams({ q }); };

  const shown = useMemo(() => {
    let list = [...results];
    const lo = parseFloat(minP), hi = parseFloat(maxP);
    if (!isNaN(lo)) list = list.filter((r) => r.price_idr != null && r.price_idr >= lo);
    if (!isNaN(hi)) list = list.filter((r) => r.price_idr != null && r.price_idr <= hi);
    if (sort === "termurah") list.sort((a, b) => (a.price_idr || 0) - (b.price_idr || 0));
    if (sort === "termahal") list.sort((a, b) => (b.price_idr || 0) - (a.price_idr || 0));
    return list;
  }, [results, sort, minP, maxP]);

  const openItem = (r, i) => nav(`/barang/${r.item_id || i}`, { state: { item: r } });

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="text-center mb-5">
        <span className="text-xs font-semibold uppercase tracking-widest text-emerald-700">Katalog</span>
        <h1 className="font-display text-3xl lg:text-4xl font-bold text-stone-900 mt-1">Cari Barang Langsung dari Ribuan Supplier China</h1>
        <p className="text-stone-500 mt-2 text-sm max-w-2xl mx-auto">Ketik nama barang pakai Bahasa Indonesia. Harga sudah dikonversi ke Rupiah.</p>
      </div>

      <form onSubmit={submit} className="flex items-center bg-white rounded-full shadow-sm border border-stone-200 p-1.5 max-w-2xl mx-auto mb-4">
        <Search size={20} className="text-stone-400 ml-3" />
        <input data-testid="ali-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Contoh: jam tangan, tas, sepatu, earphone..." className="flex-1 bg-transparent outline-none px-3 text-sm" />
        <button data-testid="ali-search-btn" disabled={state.loading} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm px-6 py-2.5 rounded-full transition flex items-center gap-2 disabled:opacity-60">
          {state.loading && <Loader2 className="animate-spin" size={16} />}Cari
        </button>
      </form>

      <div className="max-w-2xl mx-auto mb-6 text-center">
        <label data-testid="ali-image-label" className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 cursor-pointer hover:underline">
          <Camera size={16} /> Atau cari pakai foto barang
          <input data-testid="ali-image-input" type="file" accept="image/*" onChange={onImageSearch} className="hidden" />
        </label>
      </div>

      <div className="max-w-2xl mx-auto mb-6 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-xs text-amber-800">
        <Info size={15} className="mt-0.5 shrink-0" />
        <span className="leading-relaxed">Harga yang tampil adalah <b>perkiraan</b> dan belum tentu harga asli — kadang dipasang murah untuk menarik. Ajukan barang yang kamu minati, admin akan cek benar/salahnya lalu kirim total harga + ongkir final ke akunmu.</span>
      </div>

      {results.length > 0 && !member && (
        <div className="max-w-2xl mx-auto mb-6 flex items-center justify-between gap-3 bg-amber-500 text-white rounded-xl px-4 py-3" data-testid="member-cta">
          <span className="text-sm font-medium flex items-center gap-2"><Crown size={17} />Harga masih disensor. Jadi member untuk lihat harga lengkap.</span>
          <Link to="/member" className="bg-white text-amber-700 text-sm font-semibold px-3 py-1.5 rounded-lg whitespace-nowrap">Jadi Member</Link>
        </div>
      )}

      {/* Filter & sort */}
      {results.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-6 bg-white border border-stone-200 rounded-xl px-4 py-3">
          <span className="flex items-center gap-1.5 text-sm font-medium text-stone-600"><SlidersHorizontal size={15} />Filter</span>
          <div className="flex items-center gap-2 text-sm">
            <input data-testid="ali-price-min" type="number" value={minP} onChange={(e) => setMinP(e.target.value)} placeholder="Min Rp" className="w-24 border border-stone-200 rounded-lg px-2.5 py-1.5 outline-none focus:border-emerald-400" />
            <span className="text-stone-400">–</span>
            <input data-testid="ali-price-max" type="number" value={maxP} onChange={(e) => setMaxP(e.target.value)} placeholder="Max Rp" className="w-24 border border-stone-200 rounded-lg px-2.5 py-1.5 outline-none focus:border-emerald-400" />
          </div>
          <div className="flex items-center gap-2 ml-auto text-sm">
            <span className="text-stone-500">Urutkan:</span>
            <select data-testid="ali-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="border border-stone-200 rounded-lg px-3 py-1.5 outline-none focus:border-emerald-400 bg-white">
              {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <span className="text-xs text-stone-400 w-full sm:w-auto">{shown.length} barang</span>
        </div>
      )}

      {state.loading && <div className="py-16 flex justify-center text-emerald-500"><Loader2 className="animate-spin" size={30} /></div>}
      {!state.loading && state.msg && <div className="py-10 text-center text-stone-500" data-testid="ali-msg">{state.msg}</div>}
      {!state.loading && !state.done && !q && <div className="py-16 text-center text-stone-400">Ketik barang yang kamu cari di atas untuk mulai.</div>}
      {!state.loading && results.length > 0 && shown.length === 0 && <div className="py-10 text-center text-stone-500">Tidak ada barang pada rentang harga itu.</div>}

      {shown.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="ali-grid">
          {shown.map((r, i) => (
            <button key={r.item_id || i} data-testid={`ali-card-${i}`} onClick={() => openItem(r, i)}
              className="text-left bg-white rounded-2xl border border-stone-200 overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition flex flex-col">
              <div className="aspect-square bg-stone-100 overflow-hidden relative">
                {r.image ? <img src={r.image} alt={r.title} referrerPolicy="no-referrer" loading="lazy" className="w-full h-full object-cover" /> : null}
                {!member && (
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center overflow-hidden" aria-hidden="true">
                    <span className="text-white/25 font-display font-extrabold text-base sm:text-lg -rotate-[24deg] whitespace-nowrap select-none tracking-widest drop-shadow">KEDAIIMPORT · MEMBER</span>
                  </div>
                )}
              </div>
              <div className="p-3.5 flex flex-col flex-1">
                <h3 className="text-sm font-medium text-stone-800 leading-snug line-clamp-2 min-h-[2.5rem]">{r.title}</h3>
                <span className="text-[11px] bg-stone-100 text-stone-500 px-2 py-0.5 rounded-full w-fit mt-1">Min. {r.moq} pcs</span>
                <p className="text-lg font-display font-extrabold text-emerald-700 mt-2" data-testid={`ali-price-${i}`}>{r.price_idr != null ? `± ${rupiah(r.price_idr)}` : `± Rp ${r.price_masked}`}</p>
                <p className="text-[10px] text-stone-400">{r.price_idr != null ? "perkiraan / pcs · klik untuk detail" : "harga khusus member · klik untuk detail"}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
